import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { Check, ExpandMore } from '@mui/icons-material'
import { formatCompactCurrency } from '../../common/utils/backtestSummaryStats'

type BacktestRatioSelectProps = {
  // Sorted by net profit, highest first.
  summaries: any[]
  selectedGroup: string
  onChange: (group: string) => void
}

function BacktestRatioSelect({ summaries, selectedGroup, onChange }: BacktestRatioSelectProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const canChoose = summaries.length > 1
  const tradeCount =
    summaries.find((summary: any) => summary.group === selectedGroup)?.closedTradeCount || 0

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={!canChoose}
        endIcon={canChoose ? <ExpandMore sx={{ fontSize: 16 }} /> : undefined}
        aria-label={`Trade ratio ${selectedGroup}, ${tradeCount} trades`}
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation()
          setAnchorEl(e.currentTarget)
        }}
        sx={{
          minWidth: 0,
          height: 22,
          px: 1,
          fontSize: 12,
          fontWeight: 700,
          '& .MuiButton-endIcon': { ml: 0.25 },
          borderColor: 'background.custom4',
          backgroundColor: 'background.custom3',
          '&.Mui-disabled': { color: 'text.primary', borderColor: 'background.custom4' },
        }}
      >
        {selectedGroup}
        <Box component="span" sx={{ ml: 0.75, fontWeight: 500, color: 'text.secondary' }}>
          {tradeCount} trades
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 220, backgroundColor: 'background.custom3' } } }}
      >
        {summaries.map((summary: any) => {
          const profit = summary.currentProfit || 0
          const selected = summary.group === selectedGroup
          return (
            <MenuItem
              key={summary.group}
              selected={selected}
              onClick={() => {
                onChange(summary.group)
                setAnchorEl(null)
              }}
              sx={{ gap: 1, fontWeight: 600 }}
            >
              <Box sx={{ width: 16, display: 'flex' }}>
                {selected && <Check fontSize="small" color="primary" />}
              </Box>
              <Box sx={{ flexGrow: 1 }}>{summary.group}</Box>
              <Typography
                variant="body2"
                sx={{
                  minWidth: 64,
                  textAlign: 'right',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color: profit < 0 ? 'error.main' : 'primary.main',
                }}
              >
                {formatCompactCurrency(profit, true)}
              </Typography>
            </MenuItem>
          )
        })}
      </Menu>
    </Box>
  )
}

export default BacktestRatioSelect
